import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Brain, Cloud, Database, Bot, Sparkles, CheckCircle2, Terminal } from 'lucide-react';

const icons: { [key: string]: React.ElementType } = {
  Brain, Cloud, Database, Bot, Sparkles
};

export const ToolShowcase: React.FC = () => {
  const { t } = useTranslation();
  const competencies = t('toolShowcase.competencies.items', { returnObjects: true }) as any[];
  const impacts = t('toolShowcase.impact.items', { returnObjects: true }) as any[];
  const philosophyParas = t('toolShowcase.philosophy.paragraphs', { returnObjects: true }) as string[];
  const philosophyBullets = t('toolShowcase.philosophy.bullets', { returnObjects: true }) as string[];

  return (
    <section className="py-24 relative overflow-hidden bg-slate-950 text-slate-200">
      {/* Ambient Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-purple-900/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[800px] h-[800px] bg-cyan-900/10 blur-[120px] rounded-full" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-5" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-cyan-400 text-xs font-mono uppercase tracking-widest mb-6"
          >
            <Terminal size={12} />
            {t('toolShowcase.badge')}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-black tracking-tight text-white mb-6"
          >
            {t('toolShowcase.title')}
          </motion.h2>
          <motion.div 
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="h-px w-32 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto" 
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left Column: Competencies & Philosophy */}
          <div className="space-y-16">
            {/* Competencies */}
            <div>
              <motion.h3 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="text-xl font-bold text-white uppercase tracking-widest mb-8 flex items-center gap-3"
              >
                <span className="w-2 h-2 bg-cyan-500 rounded-full shadow-[0_0_10px_rgba(6,182,212,0.8)]" />
                {t('toolShowcase.competencies.title')}
              </motion.h3>
              <div className="space-y-4">
                {competencies.map((item, index) => {
                  const Icon = icons[item.icon];
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="group relative p-4 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-cyan-500/30 transition-all duration-300"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-black/50 border border-white/10 flex items-center justify-center text-cyan-400 group-hover:text-cyan-300 group-hover:scale-110 transition-all">
                          {Icon && <Icon className="w-5 h-5" />}
                        </div>
                        <p className="text-slate-300 font-light leading-relaxed mt-1.5 group-hover:text-white transition-colors">
                          {item.label}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Philosophy */}
            <div>
              <motion.h3 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="text-xl font-bold text-white uppercase tracking-widest mb-8 flex items-center gap-3"
              >
                <span className="w-2 h-2 bg-purple-500 rounded-full shadow-[0_0_10px_rgba(168,85,247,0.8)]" />
                {t('toolShowcase.philosophy.title')}
              </motion.h3>
              <div className="relative p-8 rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/5 backdrop-blur-sm">
                <div className="space-y-6">
                  {philosophyParas.map((p, i) => (
                    <p key={i} className="text-slate-300 leading-relaxed font-light border-l-2 border-purple-500/30 pl-4">
                      {p}
                    </p>
                  ))}
                  <div className="pt-4 grid gap-3">
                    {philosophyBullets.map((b, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm text-slate-400">
                        <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0" />
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column: Impact */}
          <div className="lg:sticky lg:top-32 space-y-8">
            <motion.h3 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-xl font-bold text-white uppercase tracking-widest mb-8 flex items-center gap-3"
            >
              <span className="w-2 h-2 bg-emerald-500 rounded-full shadow-[0_0_10px_rgba(16,185,129,0.8)]" />
              {t('toolShowcase.impact.title')}
            </motion.h3>
            
            <div className="grid grid-cols-1 gap-4">
              {impacts.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative overflow-hidden rounded-2xl bg-slate-900/50 border border-white/10 p-8 hover:border-emerald-500/30 transition-all duration-500"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative flex items-baseline justify-between">
                    <p className="text-5xl md:text-6xl font-black text-white tracking-tighter group-hover:text-emerald-400 transition-colors duration-300">
                      {item.value}
                    </p>
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                      <Sparkles className="w-5 h-5 text-slate-500 group-hover:text-emerald-400 transition-colors" />
                    </div>
                  </div>
                  <p className="mt-4 text-lg text-slate-400 group-hover:text-slate-200 transition-colors font-light border-t border-white/5 pt-4">
                    {item.label}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Decor */}
            <div className="mt-12 p-6 rounded-2xl border border-dashed border-white/10 bg-black/20 text-center">
              <p className="text-xs font-mono text-slate-500 uppercase tracking-widest">
                {t('toolShowcase.impact.note')}
              </p>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};


